import { useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import { observer } from 'mobx-react';

import { useCoinsAnimation } from '../../../hooks/useCoinsAnimation';
import { useHiddenBodyOverflow } from '../../../hooks/useHiddenBodyOverflow';
import { logToNewRelic } from '../../../services/debug';
import { intercom } from '../../../services/intercom';
import type { BundleItem } from '../../../stores/rewards/types';
import type { ClaimableReward } from '../../../stores/ui/types';
import { useStores } from '../../../stores/useStores';
import { devError } from '../../../utils/log';

import { LoaderCover } from './LoaderCover';
import classes from './MinigamesDialog.module.scss';

export const MinigamesDialog = observer(() => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const {
    minigamesStore,
    walletStore,
    userFreeSpinsStore,
    userStore,
    metagamesStore,
    featureManagerStore,
    userSnappyGiftsStore,
  } = useStores();
  const { startAnimation } = useCoinsAnimation();

  const [isReady, setIsReady] = useState(false);

  useHiddenBodyOverflow(true);

  const onIframeLoad = () => {
    void logToNewRelic(
      'MinigamesDialog iframe loaded',
      'MinigamesDialog iframe loaded: ' +
        JSON.stringify({
          src: iframeRef.current?.src,
        }),
      'debug',
    );
  };

  useEffect(() => {
    const eventHandler = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) return;
      try {
        const { type, payload } = event.data;
        switch (type) {
          // ─── Lifecycle ────────────────────────────────────────────────────────

          case 'gameReady': {
            void logToNewRelic(
              'MinigamesDialog game ready',
              'MinigamesDialog game ready: ' + JSON.stringify({ payload, userId: userStore.profile?.userId }),
              'debug',
            );
            minigamesStore.setCurrentGameType(payload?.gameType);
            break;
          }
          case 'authenticationComplete': {
            setIsReady(true);
            break;
          }
          case 'authenticationError': {
            setIsReady(true);
            void logToNewRelic(
              'MinigamesDialog authentication error FE',
              'MinigamesDialog authentication error FE: ' +
                JSON.stringify({ payload, userId: userStore.profile?.userId }),
              'error',
            );
            break;
          }
          case 'contactSupport': {
            intercom.handleOpenSupport();
            break;
          }
          case 'closeGame': {
            minigamesStore.closeMinigame();
            setIsReady(false);
            break;
          }
          case 'logMessage': {
            void logToNewRelic(
              'MinigamesDialog log message',
              'MinigamesDialog log message: ' + JSON.stringify({ payload, userId: userStore.profile?.userId }),
              'debug',
            );
            break;
          }

          // ─── New contract ─────────────────────────────────────────────────────

          case 'playAnimation': {
            const scAmount = payload?.rewards?.find((r: any) => r.currencyCode === 'SC')?.amount || 0;
            const gcAmount = payload?.rewards?.find((r: any) => r.currencyCode === 'GC')?.amount || 0;

            const metaGames: BundleItem[] = (payload?.rewards ?? [])
              .filter((r: any) => r.currencyCode === 'METAGAME_TOKEN')
              .map((item: any) => ({
                bundleType: 'metagameToken',
                bundlePayload: {
                  amount: item.metagameTokenAmount,
                  coinType: item.metagameTokenMode,
                  currencyName: item.metagameTokenName,
                  currencySymbol: item.metagameTokenImageUrl,
                  metagameTypeId: item.metagameTokenType,
                },
              }));

            const frbBundleItems: BundleItem[] = (payload?.rewards ?? [])
              .filter((r: any) => r.currencyCode === 'FRB')
              .map((item: any) => ({
                bundleType: 'freeSpins',
                bundlePayload: {
                  type: 'freeSpins',
                  provider: 'Barouletta Gaming',
                  bonusCode: item.bonusCode,
                  bonusCodeImageUrl: item.bonusCodeImageUrl,
                  gameImageUrl: item.bonusCodeImageUrl,
                  displayCount: Number(item.amount) || 0,
                },
              }));

            const snappyGiftBundleItems: BundleItem[] = (payload?.rewards ?? [])
              .filter((r: any) => r.currencyCode === 'SNAPPY_GIFT')
              .map((item: any, index: number) => ({
                id: `snappy-gift-${index}`,
                bundleType: 'snappyGift',
                bundlePayload: {
                  type: 'snappyGift',
                  popupImageUrl: item.snappyGiftImageUrl,
                  widgetImageUrl: item.snappyGiftImageUrl,
                  campaignId: item.snappyGiftCampaignId || '',
                },
              }));

            const bundleItems: BundleItem[] = [...metaGames, ...frbBundleItems, ...snappyGiftBundleItems];
            const rewards: ClaimableReward[] = [{ scCoin: Number(scAmount), gcCoin: Number(gcAmount), bundleItems }];

            startAnimation({
              rewards,
              startMiniGames: false,
              hideOverlay: payload?.showBackdrop === false,
            });
            break;
          }
          case 'playAnimationAndClose': {
            const scAmount = payload?.rewards?.find((r: any) => r.currencyCode === 'SC')?.amount || 0;
            const gcAmount = payload?.rewards?.find((r: any) => r.currencyCode === 'GC')?.amount || 0;

            const metaGames: BundleItem[] = (payload?.rewards ?? [])
              .filter((r: any) => r.currencyCode === 'METAGAME_TOKEN')
              .map((item: any) => ({
                bundleType: 'metagameToken',
                bundlePayload: {
                  amount: item.metagameTokenAmount,
                  coinType: item.metagameTokenMode,
                  currencyName: item.metagameTokenName,
                  currencySymbol: item.metagameTokenImageUrl,
                  metagameTypeId: item.metagameTokenType,
                },
              }));

            const frbBundleItems: BundleItem[] = (payload?.rewards ?? [])
              .filter((r: any) => r.currencyCode === 'FRB')
              .map((item: any) => ({
                bundleType: 'freeSpins',
                bundlePayload: {
                  type: 'freeSpins',
                  provider: 'Barouletta Gaming',
                  bonusCode: item.bonusCode,
                  bonusCodeImageUrl: item.bonusCodeImageUrl,
                  gameImageUrl: item.bonusCodeImageUrl,
                  displayCount: Number(item.amount) || 0,
                },
              }));

            const snappyGiftBundleItems: BundleItem[] = (payload?.rewards ?? [])
              .filter((r: any) => r.currencyCode === 'SNAPPY_GIFT')
              .map((item: any, index: number) => ({
                id: `snappy-gift-${index}`,
                bundleType: 'snappyGift',
                bundlePayload: {
                  type: 'snappyGift',
                  popupImageUrl: item.snappyGiftImageUrl,
                  widgetImageUrl: item.snappyGiftImageUrl,
                  campaignId: item.snappyGiftCampaignId || '',
                },
              }));

            const bundleItems: BundleItem[] = [...metaGames, ...frbBundleItems, ...snappyGiftBundleItems];
            const rewards: ClaimableReward[] = [{ scCoin: Number(scAmount), gcCoin: Number(gcAmount), bundleItems }];

            startAnimation({
              rewards,
              hideOverlay: payload?.showBackdrop === false,
              onEnd: () => {
                minigamesStore.closeMinigame();
                setIsReady(false);
              },
            });
            break;
          }
          case 'vibrate': {
            if (window.navigator.vibrate) {
              const duration = payload?.haptic === 'soft' ? 20 : payload?.haptic === 'hard' ? 100 : 50;
              window.navigator.vibrate(duration);
            }
            break;
          }
          case 'onError': {
            void logToNewRelic(
              'MinigamesDialog onError',
              'MinigamesDialog onError: ' + JSON.stringify({ payload, userId: userStore.profile?.userId }),
              'error',
            );
            minigamesStore.closeMinigame();
            userFreeSpinsStore.closeDialog();
            setTimeout(() => {
              const isNextDialog = userFreeSpinsStore.showNextDialog();
              if (!isNextDialog) {
                const currentFeatureType = featureManagerStore.getCurrentFeatureType();
                if (currentFeatureType) {
                  featureManagerStore.onDialogFinished(currentFeatureType);
                }
              }
            }, 500);
            break;
          }
        }
      } catch (e) {
        devError('MinigamesDialog eventHandler error', e);
      }
    };

    window.addEventListener('message', eventHandler);

    return () => {
      window.removeEventListener('message', eventHandler);
    };
  }, [
    minigamesStore,
    startAnimation,
    userFreeSpinsStore,
    userStore.profile?.userId,
    walletStore,
    metagamesStore,
    featureManagerStore,
    userSnappyGiftsStore,
  ]);

  return (
    <div className={classes.minigamesDialog}>
      <div className={classes.cover}>
        <div className={classes.dialog}>
          <iframe
            ref={iframeRef}
            className={classNames(classes.iframe, !isReady && classes.prepare)}
            src={minigamesStore.iframeData?.src || ''}
            title="Minigames"
            onLoad={onIframeLoad}
          />
        </div>
        {!isReady && <LoaderCover />}
      </div>
    </div>
  );
});
